// src/admin/pages/AdminInfo.jsx
import React, { useState, useEffect } from "react";
import { loadMainInfo, updateMainInfo } from "../../../backend/info/mainInfo";
import { socialMediaService } from "../../../services/api";
import TabBlockView from "../../components/TabBlockView";
import { toast } from "react-toastify";

// ─── Shared field save/reset logic for mainInfo fields ────────────────────────
const useMainInfoFields = (fieldNames) => {
  const empty = Object.fromEntries(fieldNames.map((k) => [k, ""]));
  const [formData, setFormData] = useState({ ...empty });
  const [originalData, setOriginalData] = useState({ ...empty });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const all = await loadMainInfo();
      const filtered = all.filter((item) => fieldNames.includes(item.name));
      const obj = { ...empty };
      filtered.forEach((item) => { obj[item.name] = item.text; });
      setFormData({ ...obj });
      setOriginalData({ ...obj });
    } catch (err) {
      setError(err.message || "Не вдалося завантажити дані");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const changed = Object.entries(formData).filter(([k, v]) => originalData[k] !== v);
    if (changed.length === 0) {
      toast.info("Немає змін для збереження");
      return;
    }
    try {
      await Promise.all(changed.map(([name, text]) => updateMainInfo(name, { text })));
      setOriginalData({ ...formData });
      toast.success("Успішно збережено!");
    } catch (err) {
      toast.error(err.message || "Помилка при збереженні");
    }
  };

  const reset = () => {
    setFormData({ ...originalData });
    toast.info("Форма скинута до збережених значень");
  };

  return { formData, loading, error, handleChange, handleSubmit, reset, fetchData };
};

// ─── Simple form section for mainInfo fields ──────────────────────────────────
const MainInfoForm = ({ fields, labelMap, placeholderMap, typeMap }) => {
  const fieldNames = fields.map((f) => f.name);
  const { formData, loading, error, handleChange, handleSubmit, reset, fetchData } =
    useMainInfoFields(fieldNames);

  if (loading) return <p style={styles.loadingText}>Завантаження...</p>;
  if (error) return (
    <div style={styles.errorContainer}>
      <p style={styles.errorText}>{error}</p>
      <button style={styles.retryButton} onClick={fetchData}>Спробувати знову</button>
    </div>
  );

  return (
    <div style={styles.formContainer}>
      <form onSubmit={handleSubmit} style={styles.form}>
        {fields.map(({ name }) => (
          <div key={name} style={styles.formGroup}>
            <label style={styles.label} htmlFor={name}>{labelMap[name]}:</label>
            <input
              style={styles.input}
              type={typeMap?.[name] || "text"}
              id={name}
              name={name}
              placeholder={placeholderMap?.[name] || ""}
              value={formData[name] || ""}
              onChange={handleChange}
            />
          </div>
        ))}
        <div style={styles.buttonContainer}>
          <button type="button" style={styles.resetButton} onClick={reset}>Скинути</button>
          <button type="submit" style={styles.button}>Зберегти</button>
        </div>
      </form>
    </div>
  );
};

// ─── Social Media Tab ─────────────────────────────────────────────────────────
const SocialMediaTab = () => {
  const [socialMedia, setSocialMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formMode, setFormMode] = useState("add");
  const [formData, setFormData] = useState({
    name: "", iconPath: "", hoverIconPath: "",
    footerIconPath: "", footerHoverIconPath: "", menuIconPath: "", redirectLink: "",
  });
  const [currentId, setCurrentId] = useState(null);

  const fetchSocialMedia = async () => {
    try {
      setLoading(true);
      const response = await socialMediaService.getAll();
      setSocialMedia(response.data);
    } catch (err) {
      setError("Не вдалося завантажити соціальні мережі");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSocialMedia(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ name: "", iconPath: "", hoverIconPath: "", footerIconPath: "", footerHoverIconPath: "", menuIconPath: "", redirectLink: "" });
    setFormMode("add");
    setCurrentId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formMode === "add") {
        await socialMediaService.create(formData);
        toast.success("Соціальну мережу успішно додано!");
      } else {
        await socialMediaService.update(currentId, formData);
        toast.success("Соціальну мережу успішно оновлено!");
      }
      resetForm();
      fetchSocialMedia();
    } catch (err) {
      toast.error(`Помилка при ${formMode === "add" ? "додаванні" : "оновленні"} соціальної мережі`);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      name: item.name || "", iconPath: item.iconPath, hoverIconPath: item.hoverIconPath,
      footerIconPath: item.footerIconPath || "", footerHoverIconPath: item.footerHoverIconPath || "",
      menuIconPath: item.menuIconPath || "", redirectLink: item.redirectLink,
    });
    setCurrentId(item.id);
    setFormMode("edit");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Ви впевнені, що хочете видалити цю соціальну мережу?")) return;
    try {
      await socialMediaService.delete(id);
      setSocialMedia((prev) => prev.filter((item) => item.id !== id));
      toast.success("Соціальну мережу успішно видалено!");
    } catch {
      toast.error("Помилка при видаленні соціальної мережі");
    }
  };

  const swapItems = async (index, direction) => {
    const other = direction === "up" ? index - 1 : index + 1;
    if (other < 0 || other >= socialMedia.length) return;
    try {
      const updated = [...socialMedia];
      const a = updated[index], b = updated[other];
      const ua = { ...a, id: b.id }, ub = { ...b, id: a.id };
      updated[index] = ub; updated[other] = ua;
      setSocialMedia(updated);
      await Promise.all([socialMediaService.update(a.id, ub), socialMediaService.update(b.id, ua)]);
      toast.success("Порядок успішно змінено!");
    } catch {
      toast.error("Помилка при зміні порядку");
      fetchSocialMedia();
    }
  };

  if (loading) return <p style={styles.loadingText}>Завантаження...</p>;
  if (error) return <p style={styles.errorText}>{error}</p>;

  return (
    <div>
      <div style={styles.formContainer}>
        <h2 style={styles.formTitle}>
          {formMode === "add" ? "Додати нову соціальну мережу" : "Редагувати соціальну мережу"}
        </h2>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroupFullWidth}>
            <label style={styles.label} htmlFor="sm-name">Назва:</label>
            <input style={styles.input} type="text" id="sm-name" name="name"
              placeholder="Facebook" value={formData.name} onChange={handleChange} required />
          </div>
          {[
            { name: "iconPath", label: "Іконка", placeholder: "/assets/social/facebook.svg" },
            { name: "hoverIconPath", label: "Іконка (hover)", placeholder: "/assets/social/facebook-hover.svg" },
            { name: "footerIconPath", label: "Іконка футер", placeholder: "/assets/social/footer/facebook.svg" },
            { name: "footerHoverIconPath", label: "Іконка футер (hover)", placeholder: "/assets/social/footer/facebook-hover.svg" },
            { name: "menuIconPath", label: "Іконка меню", placeholder: "/assets/social/menu/facebook.svg" },
          ].map(({ name, label, placeholder }) => (
            <div key={name} style={styles.formGroup}>
              <label style={styles.label} htmlFor={`sm-${name}`}>{label}:</label>
              <input style={styles.input} type="text" id={`sm-${name}`} name={name}
                placeholder={placeholder} value={formData[name]} onChange={handleChange} required />
            </div>
          ))}
          <div style={styles.formGroupFullWidth}>
            <label style={styles.label} htmlFor="sm-redirectLink">Посилання:</label>
            <input style={styles.input} type="url" id="sm-redirectLink" name="redirectLink"
              placeholder="https://facebook.com/..." value={formData.redirectLink} onChange={handleChange} required />
          </div>
          <div style={styles.buttonContainer}>
            {formMode === "edit" && (
              <button type="button" style={styles.cancelButton} onClick={resetForm}>Скасувати</button>
            )}
            <button type="submit" style={styles.button}>
              {formMode === "add" ? "Додати" : "Зберегти зміни"}
            </button>
          </div>
        </form>
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Назва</th>
              <th style={styles.tableHeader}>Іконки</th>
              <th style={styles.tableHeader}>Посилання</th>
              <th style={styles.tableHeader}>Позиція</th>
              <th style={styles.tableHeader}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {socialMedia.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: "center" }}>Немає доданих соціальних мереж</td></tr>
            ) : (
              socialMedia.map((item, index) => (
                <tr key={item.id} style={styles.tableRow}>
                  <td style={styles.tableCell}>{item.name || "-"}</td>
                  <td style={styles.tableCell}>
                    <div style={styles.iconPreview}>
                      <div style={styles.iconGroup}>
                        <span style={styles.iconLabel}>Звичайні:</span>
                        <img style={styles.iconImage} src={item.iconPath} alt="Icon" />
                        <img style={styles.iconImage} src={item.hoverIconPath} alt="Hover" />
                      </div>
                      {(item.footerIconPath || item.footerHoverIconPath) && (
                        <div style={styles.iconGroup}>
                          <span style={styles.iconLabel}>Футер:</span>
                          {item.footerIconPath && <img style={styles.iconImage} src={item.footerIconPath} alt="Footer" />}
                          {item.footerHoverIconPath && <img style={styles.iconImage} src={item.footerHoverIconPath} alt="Footer Hover" />}
                        </div>
                      )}
                      {item.menuIconPath && (
                        <div style={styles.iconGroup}>
                          <span style={styles.iconLabel}>Меню:</span>
                          <img style={styles.iconImage} src={item.menuIconPath} alt="Menu" />
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={styles.tableCell}>
                    <a href={item.redirectLink} target="_blank" rel="noopener noreferrer" style={styles.link}>
                      {item.redirectLink}
                    </a>
                  </td>
                  <td style={styles.tableCell}>
                    <div style={styles.positionButtonsContainer}>
                      <button style={styles.positionButton} onClick={() => swapItems(index, "up")} disabled={index === 0} title="Вгору">▲</button>
                      <button style={styles.positionButton} onClick={() => swapItems(index, "down")} disabled={index === socialMedia.length - 1} title="Вниз">▼</button>
                    </div>
                  </td>
                  <td style={styles.tableCell}>
                    <a style={styles.actionLink} onClick={() => handleEdit(item)}>Редагувати</a>
                    <a style={styles.actionLinkDanger} onClick={() => handleDelete(item.id)}>Видалити</a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const AdminInfo = () => (
  <div style={styles.pageContainer}>
    <h1 style={styles.pageTitle}>Контакти та соціальні мережі</h1>
    <TabBlockView
      tabs={[
        {
          name: "Соцмережі",
          content: <SocialMediaTab />,
        },
        {
          name: "Телефони",
          content: (
            <MainInfoForm
              fields={[
                { name: "recruitmentPhone" },
                { name: "cvsPhone" },
              ]}
              labelMap={{
                recruitmentPhone: "Відділення рекрутингу",
                cvsPhone: "Служба ЦВС",
              }}
              placeholderMap={{
                recruitmentPhone: "+38 (XXX) XXX-XX-XX",
                cvsPhone: "+38 (XXX) XXX-XX-XX",
              }}
              typeMap={{
                recruitmentPhone: "tel",
                cvsPhone: "tel",
              }}
            />
          ),
        },
        {
          name: "Пошта",
          content: (
            <MainInfoForm
              fields={[
                { name: "pressEmail" },
                { name: "feedbackEmail" },
                { name: "applicationEmail" },
              ]}
              labelMap={{
                pressEmail: "Пресслужба",
                feedbackEmail: "Анонімний відгук",
                applicationEmail: "Анкета на вступ",
              }}
              placeholderMap={{
                pressEmail: "press@example.com",
                feedbackEmail: "feedback@example.com",
                applicationEmail: "application@example.com",
              }}
              typeMap={{
                pressEmail: "email",
                feedbackEmail: "email",
                applicationEmail: "email",
              }}
            />
          ),
        },
        {
          name: "Підтримка",
          content: (
            <MainInfoForm
              fields={[{ name: "supportLink" }, { name: "subscriptionLink" }]}
              labelMap={{
                supportLink: "Посилання на банку",
                subscriptionLink: "Підписка",
              }}
              placeholderMap={{
                supportLink: "https://send.monobank.ua/jar/...",
                subscriptionLink: "https://send.monobank.ua/...",
              }}
              typeMap={{
                supportLink: "url",
                subscriptionLink: "url",
              }}
            />
          ),
        },
        {
          name: "Інше",
          content: (
            <MainInfoForm
              fields={[{ name: "headerButtonName" }, { name: "headerButtonLink" }, { name: "soldierPhotoLink" }, { name: "anketaLink" }]}
              labelMap={{
                headerButtonName: "Назва кнопки в хедері",
                headerButtonLink: "Посилання кнопки в хедері",
                soldierPhotoLink: "Фото солдата",
                anketaLink: "Посилання на анкету",
              }}
              placeholderMap={{
                headerButtonName: "ПРИЄДНАТИСЯ",
                headerButtonLink: "https://example.com",
                soldierPhotoLink: "assets/photo1.png",
                anketaLink: "https://210polk.mil.gov.ua/anketa",
              }}
              typeMap={{
                headerButtonName: "text",
                headerButtonLink: "text",
                soldierPhotoLink: "text",
                anketaLink: "text",
              }}
            />
          ),
        },
      ]}
    />
  </div>
);

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles: Record<string, React.CSSProperties> = {
  pageContainer: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    padding: "2rem",
  },
  pageTitle: {
    marginBottom: "1.5rem",
    fontSize: "1.8rem",
    color: "var(--secondary-color)",
  },
  formContainer: {
    marginBottom: "2rem",
    padding: "1.5rem",
    backgroundColor: "#e9e9e9",
    borderRadius: "8px",
  },
  formTitle: {
    marginBottom: "1rem",
    fontSize: "1.3rem",
    color: "var(--secondary-color)",
  },
  form: {
    display: "grid",
    gap: "1rem",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  formGroupFullWidth: {
    display: "flex",
    flexDirection: "column",
    gridColumn: "1 / -1",
  },
  label: {
    marginBottom: "0.5rem",
    fontWeight: 600,
  },
  input: {
    padding: "0.8rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    outline: "none",
  },
  button: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "green",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  cancelButton: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "#FFBB00",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background-color 0.3s",
    marginRight: "1rem",
  },
  resetButton: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
    transition: "background-color 0.3s",
    marginRight: "1rem",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gridColumn: "1 / -1",
  },
  tableContainer: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  tableHeader: {
    padding: "0.8rem",
    textAlign: "left",
    borderBottom: "1px solid #eee",
    backgroundColor: "#e8e8e8",
    fontWeight: 600,
  },
  tableRow: { borderBottom: "1px solid #eee" },
  tableCell: { padding: "0.8rem", textAlign: "left", borderBottom: "1px solid #eee" },
  actionLink: { color: "var(--primary-color)", textDecoration: "none", cursor: "pointer", marginRight: "1rem" },
  actionLinkDanger: { color: "var(--error-color)", textDecoration: "none", cursor: "pointer", marginRight: "1rem" },
  loadingText: { padding: "2rem", textAlign: "center", color: "#666" },
  loadingContainer: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" },
  errorContainer: { display: "flex", flexDirection: "column", alignItems: "center", minHeight: "200px", justifyContent: "center" },
  errorText: { color: "#dc3545", marginBottom: "1rem", fontSize: "1.1rem" },
  retryButton: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    cursor: "pointer",
  },
  iconPreview: { display: "flex", flexDirection: "column", gap: "0.75rem" },
  iconGroup: { display: "flex", alignItems: "center", gap: "0.75rem" },
  iconLabel: { fontSize: "0.85rem", color: "#555", minWidth: "70px" },
  iconImage: {
    width: "32px", height: "32px", objectFit: "contain", padding: "2px",
    border: "1px solid #eee", borderRadius: "4px", backgroundColor: "#f9f9f9",
  },
  link: { color: "var(--primary-color)", wordBreak: "break-all" },
  positionButtonsContainer: { display: "flex", flexDirection: "column", gap: "0.5rem" },
  positionButton: {
    width: "30px", height: "30px", display: "flex", justifyContent: "center", alignItems: "center",
    backgroundColor: "#f0f0f0", border: "1px solid #ddd", borderRadius: "4px",
    cursor: "pointer", fontSize: "12px", padding: 0, lineHeight: 1,
  },
};

export default AdminInfo;